// Profanity filter for user-visible inputs.
//
// Two strictness levels:
//   'strict'  — substring match after normalisation. Use for usernames where
//               "adolfhitler" or "n1gger" must be caught. Default.
//   'normal'  — word-boundary match. Use for chat/guesses so "assassin" or
//               "therapist" are not false-positives.
//
// Normalisation pipeline (applied before matching):
//   1. Lowercase
//   2. Strip non-alphanumeric separators between letters (dots, dashes,
//      underscores, spaces) so "h.i.t" → "hit"
//   3. Leet-speak substitution: 0→o, 1/!/|→i, 3→e, 4→a, 5→s, 7→t,
//      $→s, @→a, +→t, ph→f
//   4. Collapse repeated characters ("niiigger" → "niger" → caught)

// ── Blocked word list ──────────────────────────────────────────────
// Only truly unacceptable terms. Does NOT block crude-but-legitimate
// words (ass, cock, bitch, pussy, tit, butt, crack…) because these
// may be valid drawing-game guesses.
var BLOCKED = [
    // Racial / ethnic slurs
    'nigger', 'nigga', 'nig', 'kike', 'spic', 'spick', 'chink', 'gook',
    'beaner', 'wetback', 'coon', 'jigaboo', 'pickaninny', 'sambo',
    'zipperhead', 'raghead', 'sandnigger', 'towelhead', 'redskin',
    // Homophobic / transphobic
    'faggot', 'fagg', 'fag', 'dyke', 'tranny', 'shemale',
    // Ableist (severe)
    'retard', 'retarded',
    // Sexist / misogynistic
    'cunt', 'slut', 'whore', 'twat',
    // Explicit sexual (purely pornographic, not body-part words)
    'cumshot', 'cumslut', 'jizz', 'gangbang', 'blowjob', 'handjob',
    'rimjob', 'creampie', 'deepthroat', 'facefuck', 'goatse',
    // Hate / extremism
    'hitler', 'heil', 'auschwitz', 'holocaust',
    'kkk', 'nazism', 'jihadi',
    // Shock / gore
    'snuff',
];

// ── Internal helpers ───────────────────────────────────────────────

function _normalise(text) {
    var s = text.toLowerCase();
    // Strip separators between word chars: "h.i.t.l.e.r" → "hitler"
    s = s.replace(/([a-z0-9])[_.\-\s]+(?=[a-z0-9])/g, '$1');
    // Leet substitutions (order matters for multi-char like 'ph')
    s = s.replace(/ph/g, 'f');
    s = s.replace(/0/g, 'o');
    s = s.replace(/[1!|]/g, 'i');
    s = s.replace(/3/g, 'e');
    s = s.replace(/4/g, 'a');
    s = s.replace(/5/g, 's');
    s = s.replace(/7/g, 't');
    s = s.replace(/\$/g, 's');
    s = s.replace(/@/g, 'a');
    s = s.replace(/\+/g, 't');
    // Collapse repeated letters: "niiiggger" → "niger"
    s = s.replace(/(.)\1+/g, '$1');
    return s;
}

// Precompute normalised blocked list (collapsed too, so "nigger" → "niger")
var _normBlocked = BLOCKED.map(function (w) { return _normalise(w); });
// Deduplicate after normalisation and filter out patterns that are too short
// (e.g. "kkk" collapses to "k" which would match everything).
var _uniqueNorm = [];
var _seen = {};
for (var i = 0; i < _normBlocked.length; i++) {
    if (!_seen[_normBlocked[i]] && _normBlocked[i].length >= 3) {
        _seen[_normBlocked[i]] = true;
        _uniqueNorm.push(_normBlocked[i]);
    }
}

// Words that collapse too aggressively need pre-collapse matching.
// Build patterns that allow repeated chars: "kkk" → "k+k+k+"
var _repeatPatterns = [];
for (var j = 0; j < BLOCKED.length; j++) {
    var norm = _normalise(BLOCKED[j]);
    if (norm.length < 3) {
        // Build pattern from original (lowercased) that allows char repetition
        var orig = BLOCKED[j].toLowerCase();
        var pat = orig.split('').map(function (ch) {
            return ch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '+';
        }).join('');
        _repeatPatterns.push(pat);
    }
}

// Regex that matches any blocked word as a substring (strict mode)
var _substringPattern = new RegExp(
    _uniqueNorm.map(function (w) { return w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }).join('|'),
    'i'
);

// Regex that matches blocked words only at word boundaries (normal mode)
var _wordPattern = new RegExp(
    '(?:^|[\\s\\W])(' +
    _uniqueNorm.map(function (w) { return w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }).join('|') +
    ')(?:[\\s\\W]|$)',
    'i'
);

// Pre-collapse patterns for words that collapse too aggressively (e.g. "kkk")
var _repeatSubstringPattern = _repeatPatterns.length > 0
    ? new RegExp(_repeatPatterns.join('|'), 'i') : null;
var _repeatWordPattern = _repeatPatterns.length > 0
    ? new RegExp('(?:^|[\\s\\W])(' + _repeatPatterns.join('|') + ')(?:[\\s\\W]|$)', 'i') : null;

// ── Public API ─────────────────────────────────────────────────────

/**
 * Check whether text contains profanity.
 *
 * @param {string}  text
 * @param {object}  [opts]
 * @param {'strict'|'normal'} [opts.mode='strict']
 *   'strict'  — substring match; catches "adolfhitler". Best for usernames.
 *   'normal'  — word-boundary match; "assassin" is OK. Best for chat.
 * @returns {boolean} true if blocked content is found
 */
export function containsProfanity(text, opts) {
    if (!text) return false;
    var mode = (opts && opts.mode) || 'strict';
    var norm = _normalise(text);
    // Normalise without collapse for repeat-pattern matching
    var preCollapse = text.toLowerCase()
        .replace(/([a-z0-9])[_.\-\s]+(?=[a-z0-9])/g, '$1')
        .replace(/ph/g, 'f').replace(/0/g, 'o').replace(/[1!|]/g, 'i')
        .replace(/3/g, 'e').replace(/4/g, 'a').replace(/5/g, 's')
        .replace(/7/g, 't').replace(/\$/g, 's').replace(/@/g, 'a').replace(/\+/g, 't');
    if (mode === 'strict') {
        return _substringPattern.test(norm)
            || (_repeatSubstringPattern !== null && _repeatSubstringPattern.test(preCollapse));
    }
    // normal: pad with spaces so the word-boundary anchors work at edges
    return _wordPattern.test(' ' + norm + ' ')
        || (_repeatWordPattern !== null && _repeatWordPattern.test(' ' + preCollapse + ' '));
}
